import React, {Component} from 'react';
import css from './CheckboxDropDown.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import onClickOutside from 'react-onclickoutside';


/**
 * @class
 *
 * A multi-select dropdown component where multiple values are selected via checkboxes.
 *
 * Common use cases include pages where we'd want to filter lists by multiple properties from a list of filterable options.
 * Whenever the user checks/unchecks an option in the dropdown, the component returns an updated list (of objects),
 * indicating which options are currently selected -- in other words, it updates the `checked` property inside the objects, and returns the entire list.
 *
 * @prop onDropdownSelect {function}: Handler for when an option is selected. You should pass options into the callback function. When selected options changes,
 * the component returns an updated list (of objects). The `onDropdownSelect` handler should use this return (e.g. iterate through which options are checked and do something afterwards).
 * @prop text {string}: The label/text on the button.
 * @prop options {array}: Array of objects. Each object in array has the following properties (n.b. case sensitivity):
 *      label {string}: The displayed label/text of the option.
 *      value {string}: The value of the option (as opposed to the displayed text).
 *      checked {boolean}: Indicated whether the option is selected.
 * @attribute button-outline: When the `button-outline` attribute is indicated on the component tag, the button styling is changed to a transparent "outline".
 *
 * @example
 *  // markup:
 *  <CheckboxDropDown
 *      button-outline
 *      options={this.state.filterOptions}
 *      onDropdownSelect={(options) => this.filterOptionsOnChange(options)}
 *      text="Foo"
 *  />
 *
 *  // onDropdownSelect handler:
 *  // n.b. for convenience, we use an additional `selectedOptions` state variable (which is an array of strings), which makes certain filtering a bit easier:
 *  filterOptionsOnChange = (options) => {
 *       const selectedOptions = options
 *           .filter(item => item.checked)
 *           .map(item => item.value);
 *
 *       this.setState({
 *           filterStagesOptions: options,
 *           selectedOptions,
 *       });
 *  };
 */
const CheckboxDropDown = class extends Component {
    // ---------- state: ----------
    state = {
        isActive: false
    };

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf CheckboxDropDown
     *
     * Helper method for toggling `isActive` state variable.
     */
    toggleDropDown = () => {
        this.setState({
            isActive: !this.state.isActive
        });
    };

    /**
     * @method
     * @methodOf CheckboxDropDown
     *
     * Handler for when user clicks outside the component -- hides the dropdown.
     *
     * This is an out-of-the-box handler provided by rect-onclickoutside <https://www.npmjs.com/package/react-onclickoutside#es6-class-component>.
     * Anytime the user clicks outside the component, the handler is called.
     */
    handleClickOutside = () => {
        this.setState({isActive: false});
    };

    /**
     * @method
     * @methodOf CheckboxDropDown
     *
     * Handler for when user clicks on a checkbox in the dropdown -- fires the `onDropdownSelect` prop, passing the updated list of options.
     *
     * @param updatedOption {object}: The selected option from the list of `options` prop.
     */
    optionOnChange = (updatedOption) => {
        const updatedOptions = this.props.options.map(option => {
            if (updatedOption.value === option.value) {
                option.checked = !option.checked;
            }

            return option;
        });

        this.props.onDropdownSelect(updatedOptions);
    };

    /**
     * @method
     * @methodOf CheckboxDropDown
     *
     * Handler for when user clicks the "All" button -- checks all options and fires the `onDropdownSelect` prop,
     * passing in the updated options list.
     */
    btnSelectAllOnClick = () => {
        const options = this.props.options.map(option => {
            option.checked = true;

            return option;
        });

        this.props.onDropdownSelect(options);
        this.toggleDropDown();
    };

    /**
     * @method
     * @methodOf CheckboxDropDown
     *
     * Handler for when user clicks the "None" button -- unchecks all options and fires the `onDropdownSelect` prop,
     * passing in the updated options list.
     */
    btnSelectNoneOnClick = () => {
        const options = this.props.options.map(option => {
            option.checked = false;

            return option;
        });

        this.props.onDropdownSelect(options);
        this.toggleDropDown();
    };


    render() {
        return(
            <div className={css.CheckboxDropDown}>
                <button className={this.props["button-outline"] ? "btn btn-outline w-100" : "btn btn-primary w-100"} type="button" onClick={() => this.toggleDropDown()}>
                    <span>{ this.props.text }</span>
                    <FontAwesomeIcon className="ml-2" icon={faChevronDown} />
                </button>
                <div className={css.CheckboxDropDown__List} style={{ display: this.state.isActive ? "block" : "none" }}>

                    {/* ---------- CTAs: ---------- */}
                    <div className="px-3 pt-3">
                        <button className="btn btn-primary p-2" type="button" onClick={() => this.btnSelectAllOnClick()}>All</button>
                        <button className="btn btn-primary p-2 ml-3" type="button" onClick={() => this.btnSelectNoneOnClick()}>None</button>
                    </div>

                    {/* ---------- drop-down list: ---------- */}
                    <ul>
                        {
                            this.props.options.map((option, index) => {
                                return (
                                    <li
                                        className={css.CheckboxDropDown__ListItem}
                                        key={index}
                                    >
                                        <label className={css.CheckboxDropDown__Checkbox}>
                                            <input type="checkbox" value={option.value} checked={option.checked} onChange={() => this.optionOnChange(option)} />
                                            <span className="ml-2">{ option.label }</span>
                                        </label>
                                    </li>
                                );
                            })
                        }
                    </ul>
                </div>
            </div>
        );
    }
}

// In order to use react-onclickoutside, we wrap the component -- see <https://www.npmjs.com/package/react-onclickoutside#usage>:
export default onClickOutside(CheckboxDropDown);
